import type { NostrEvent } from '@nostrify/nostrify';

/**
 * Hashtags are how the archive's posts are labelled on Nostr, and the tagging
 * system reads them (see noteTags.ts), but the archive itself never shows them:
 * the labels it shows are its own. The one exception is the pager-intercept
 * marker, which is part of how those records are cited.
 */
export const SHOWN_HASHTAGS: ReadonlySet<string> = new Set(['911txts']);

/**
 * What counts as a hashtag. It must start a word and contain a letter, so
 * "#NYC" is one but "#7" (as in "Building #7"), "C#" and "page#top" are
 * ordinary text and are left alone. Group 1 is whatever precedes the "#" (the
 * start of a line, spaces, or one punctuation mark); group 2 is the tag.
 * (No lookbehind: older iPhones cannot parse it, and the whole site would fail.)
 * Apply to text that has had its links removed.
 */
const HASHTAG_SOURCE = '(^|[^\\S\\n]+|[^\\p{L}\\p{N}_&/\\s])#(?=[\\p{N}_]*\\p{L})([\\p{L}\\p{N}_]+)';

/** Is this hashtag one of the few the archive shows? Accepts it with or without the #. */
export function isShownHashtag(tag: string): boolean {
  return SHOWN_HASHTAGS.has(tag.trim().replace(/^#/, '').toLowerCase());
}

/** Every hashtag in the text, lower-cased and without the "#". */
export function findHashtags(text: string): string[] {
  return Array.from(text.matchAll(new RegExp(HASHTAG_SOURCE, 'gmu')), (m) => m[2].toLowerCase());
}

/** The text with hashtags taken out (and the spaces that led into them). */
export function stripHashtags(text: string, { keepShown = false } = {}): string {
  return text.replace(new RegExp(HASHTAG_SOURCE, 'gmu'), (whole, lead: string, tag: string) => {
    if (keepShown && isShownHashtag(tag)) return whole;
    // Keep a leading punctuation mark such as "(", drop plain spacing.
    return /^\s*$/.test(lead) ? '' : lead;
  });
}

/** The text with every hashtag taken out, except the ones the archive shows. */
export function hideHashtags(text: string): string {
  return stripHashtags(text, { keepShown: true });
}

/**
 * Is the "#tag" found at `index` in `text` really a hashtag? For callers that
 * walk the text themselves (the record page): it must not be glued to a word
 * before it, and must contain a letter.
 */
export function isHashtagAt(text: string, index: number, tag: string): boolean {
  const before = index > 0 ? text[index - 1] : '';
  if (before && /[\p{L}\p{N}_&/]/u.test(before)) return false;
  return /\p{L}/u.test(tag);
}

/**
 * Extract a plain-text excerpt from a note's content,
 * stripping nostr: URIs, URLs, hashtags, and trimming to a given length.
 */
export function getNoteExcerpt(event: NostrEvent, maxLength = 280): string {
  let text = event.content;
  // Remove nostr: URIs
  text = text.replace(/nostr:[a-z0-9]+/gi, '');
  // Remove bare URLs
  text = text.replace(/https?:\/\/\S+/gi, '');
  // Remove hashtags (the labels are shown by the archive itself)
  text = hideHashtags(text);
  // Collapse whitespace
  text = text.replace(/\s+/g, ' ').trim();
  if (text.length > maxLength) {
    return text.slice(0, maxLength).replace(/\s+\S*$/, '') + '…';
  }
  return text;
}

/**
 * The longest opening paragraph still read as a title. Anything longer is prose
 * (a statement, a quotation, an article's first paragraph), and giving it a bold
 * headline would be making one up.
 */
export const TITLE_MAX_CHARS = 140;

/** The longest title taken from an explicit `title` or `subject` tag. */
const TAGGED_TITLE_MAX_CHARS = 200;

/**
 * One paragraph of a note as readable text: no links, Nostr references or
 * hashtags. (A kept #911txts survives as plain text, which is why a paragraph
 * that is only that marker still counts as a paragraph.)
 */
export function cleanBlock(block: string): string {
  return hideHashtags(block.replace(NOSTR_REF_RE, ' ').replace(URL_RE, ' '))
    .replace(/\s+/g, ' ')
    .trim();
}

/** A title with any markdown heading or bold markers taken off. The words are never changed. */
export function plainTitle(text: string): string {
  return text
    .replace(/^#{1,6}\s+/, '')
    .replace(/^(\*{1,2}|_{1,2})(.+)\1$/, '$2')
    .trim();
}

export interface NoteHeading {
  /** A bold headline for the record, or null when the note doesn't open with one. */
  title: string | null;
  /** The rest of the writing, trimmed for a card. Empty when there is nothing more to say. */
  description: string;
}

/**
 * Find a record's title, the way a video has one, and the small description
 * that goes under it.
 *
 *  1. An explicit `title` (or NIP-14 `subject`) tag wins, when a note has one.
 *  2. Otherwise the title is the note's opening paragraph, if it is short enough
 *     to be a headline (up to {@link TITLE_MAX_CHARS} characters). Paragraphs
 *     are split on blank lines, so a single hard-wrapped paragraph never gets
 *     cut in two.
 *  3. A note that opens with a long paragraph has no title. Nothing is invented:
 *     the text is simply shown as it always was.
 *
 * The description is whatever writing is left, with links and hashtags taken
 * out (the hashtags are already shown as labels). It is empty when the title
 * was the whole note. The wording is never altered.
 */
export function getNoteHeading(event: NostrEvent, descriptionMax = 160): NoteHeading {
  const blocks = (event.content ?? '').split(/\n\s*\n/).map(cleanBlock).filter(Boolean);

  const tagged = event.tags
    .find(([name, value]) => (name === 'title' || name === 'subject') && value?.trim())?.[1]
    ?.trim();

  let title: string | null = null;
  let rest = blocks;

  if (tagged && tagged.length <= TAGGED_TITLE_MAX_CHARS) {
    title = plainTitle(tagged);
    // The note's own text often repeats the title as its first line.
    if (blocks[0] && plainTitle(blocks[0]).toLowerCase() === title.toLowerCase()) rest = blocks.slice(1);
  } else if (blocks[0]) {
    const first = plainTitle(blocks[0]);
    if (first.length >= 3 && first.length <= TITLE_MAX_CHARS) {
      title = first;
      rest = blocks.slice(1);
    }
  }

  let description = rest.join(' ').replace(/\s+/g, ' ').trim();
  if (description.length > descriptionMax) {
    description = description.slice(0, descriptionMax).replace(/\s+\S*$/, '') + '…';
  }
  return { title, description };
}

/**
 * Extract a thumbnail URL for use on cards.
 * Priority:
 *   1. imeta tag with image/* mime
 *   2. imeta tag with a `thumb` field
 *   3. Explicit image extension in content
 *   4. Any imeta URL that is NOT a known video mime (renders with onError fallback)
 *   5. Extensionless CDN URL in content
 * Returns null if nothing usable found.
 */
export function getNoteImage(event: NostrEvent): string | null {
  let firstImetaUrl: string | null = null;

  for (const tag of event.tags) {
    if (tag[0] !== 'imeta') continue;
    const vals = tag.slice(1);
    let tagUrl: string | null = null;
    let tagMime: string | null = null;
    let tagThumb: string | null = null;

    for (const v of vals) {
      if (v.startsWith('url ')) tagUrl = v.slice(4).trim();
      if (v.startsWith('m ')) tagMime = v.slice(2).trim();
      if (v.startsWith('thumb ')) tagThumb = v.slice(6).trim();
    }

    // Prefer an imeta thumb field
    if (tagThumb) return tagThumb;

    if (tagUrl) {
      if (tagMime) {
        if (tagMime.startsWith('image/')) return tagUrl; // definite image
        if (tagMime.startsWith('video/')) {
          // It's a video — skip unless nothing else found
          if (!firstImetaUrl) firstImetaUrl = null; // don't use video URLs as thumbnails
          continue;
        }
      }
      // No mime info — stash as fallback
      if (!firstImetaUrl) firstImetaUrl = tagUrl;
    }
  }

  // Check content for an explicit image extension
  const imgMatch = event.content.match(
    /https:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|avif|svg)(?:\?[^\s]*)?/i,
  );
  if (imgMatch) return imgMatch[0];

  // Fall back to the first imeta URL with unknown mime (non-video)
  if (firstImetaUrl) return firstImetaUrl;

  // Last resort: extensionless CDN URL in content
  const cdnMatch = event.content.match(
    /https:\/\/(?:m\.primal\.net|void\.cat|nostr\.build|cdn\.nostr\.build|blossom\.[^\s/]+|files\.v0l\.io|nostrcheck\.me|cdn\.satellite\.earth|media\.nostr\.band)\/[^\s]+/i,
  );
  return cdnMatch ? cdnMatch[0] : null;
}

const IMAGE_URLS_RE = /https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|avif)(?:\?[^\s]*)?/gi;

/**
 * How many pictures a note carries (from its text and its attachments). A card
 * only shows the first one, so this is what lets it say "18 images" when a
 * record is really a long document photographed page by page.
 */
export function getNoteImageCount(event: NostrEvent): number {
  const urls = new Set<string>();
  for (const m of (event.content ?? '').matchAll(IMAGE_URLS_RE)) {
    urls.add(m[0].replace(/[),.;:!?]+$/, ''));
  }
  for (const tag of event.tags) {
    if (tag[0] !== 'imeta') continue;
    let url: string | null = null;
    let mime: string | null = null;
    for (const v of tag.slice(1)) {
      if (v.startsWith('url ')) url = v.slice(4).trim();
      if (v.startsWith('m ')) mime = v.slice(2).trim();
    }
    if (url && mime?.startsWith('image/')) urls.add(url);
  }
  return urls.size;
}

/**
 * Extract the first video URL from a note (imeta video/* mime, or video
 * extension in content, or extensionless CDN URL referenced by an imeta
 * tag whose mime is video). Returns null if none found.
 */
export function getNoteVideo(event: NostrEvent): string | null {
  // imeta with video/* mime
  for (const tag of event.tags) {
    if (tag[0] !== 'imeta') continue;
    const vals = tag.slice(1);
    let tagUrl: string | null = null;
    let tagMime: string | null = null;
    for (const v of vals) {
      if (v.startsWith('url ')) tagUrl = v.slice(4).trim();
      if (v.startsWith('m ')) tagMime = v.slice(2).trim();
    }
    if (tagUrl && tagMime?.startsWith('video/')) return tagUrl;
  }

  // Video extension in content
  const extMatch = event.content.match(
    /https:\/\/[^\s]+\.(?:mp4|webm|mov|m4v|mkv|ogg)(?:\?[^\s]*)?/i,
  );
  if (extMatch) return extMatch[0];

  return null;
}

/**
 * The pixel size of a note's picture, when its attachment tag says so
 * (`imeta` … `dim 1200x800`). Lets a card reserve exactly the right space
 * before the picture arrives, so nothing jumps when it loads. Returns null when
 * the note doesn't say.
 */
export function getNoteImageSize(
  event: NostrEvent,
  imageUrl: string | null,
): { width: number; height: number } | null {
  if (!imageUrl) return null;
  for (const tag of event.tags) {
    if (tag[0] !== 'imeta') continue;
    let url: string | null = null;
    let thumb: string | null = null;
    let dim: string | null = null;
    for (const v of tag.slice(1)) {
      if (v.startsWith('url ')) url = v.slice(4).trim();
      if (v.startsWith('thumb ')) thumb = v.slice(6).trim();
      if (v.startsWith('dim ')) dim = v.slice(4).trim();
    }
    if (!dim || (url !== imageUrl && thumb !== imageUrl)) continue;
    const m = dim.match(/^(\d{1,5})x(\d{1,5})$/);
    if (!m) continue;
    const width = Number(m[1]);
    const height = Number(m[2]);
    // The size of the full picture is only right for the full picture, not a thumb.
    if (width > 0 && height > 0 && url === imageUrl) return { width, height };
  }
  return null;
}

/** File types we treat as documents (as opposed to pictures, video or web pages). */
const DOCUMENT_EXT = 'pdf|docx?|rtf|odt|txt|epub';
const DOCUMENT_URL_RE = new RegExp(`\\.(?:${DOCUMENT_EXT})(?:[?#]|$)`, 'i');
const DOCUMENT_IN_TEXT_RE = new RegExp(`https?://[^\\s]+\\.(?:${DOCUMENT_EXT})(?:[?#][^\\s]*)?`, 'i');

/** Is this URL a document file (PDF, Word, plain text…)? */
export function isDocumentUrl(url: string): boolean {
  return DOCUMENT_URL_RE.test(url);
}

/**
 * The first document link in a note (PDF, Word, text, EPUB…), from its text or
 * its attachments. Returns null if there is none.
 */
export function getNoteDocument(event: NostrEvent): string | null {
  for (const tag of event.tags) {
    if (tag[0] !== 'imeta') continue;
    let url: string | null = null;
    let mime: string | null = null;
    for (const v of tag.slice(1)) {
      if (v.startsWith('url ')) url = v.slice(4).trim();
      if (v.startsWith('m ')) mime = v.slice(2).trim();
    }
    if (url && (mime === 'application/pdf' || isDocumentUrl(url))) return url;
  }
  return event.content.match(DOCUMENT_IN_TEXT_RE)?.[0] ?? null;
}

export const URL_RE = /https?:\/\/[^\s]+/gi;
/** `nostr:note1…`, `nevent1…`, `naddr1…` and friends, with or without the `nostr:` prefix. */
export const NOSTR_REF_RE = /\b(?:nostr:)?(?:note|nevent|naddr|nprofile|npub)1[a-z0-9]{20,}/gi;

/**
 * Prose at least this long (links, Nostr references and hashtags not counted)
 * is a written piece, not a caption. Used by the archive filter (long-form is
 * always kept) and by the Text format.
 */
export const LONG_FORM_MIN_CHARS = 400;

/**
 * A note with no picture, video or document needs only this much writing to be
 * a text record (a pager intercept or a short statement, for example).
 */
export const TEXT_ONLY_MIN_CHARS = 60;

/** The writing in a note, with links, Nostr references and hashtags taken out. */
export function getNoteProse(event: NostrEvent): string {
  return stripHashtags((event.content ?? '').replace(URL_RE, ' ').replace(NOSTR_REF_RE, ' '))
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Is the writing itself the record? True for a note with no picture, video or
 * document and at least a sentence or two of prose, and for any note with a
 * long written piece (even if it also carries a picture).
 */
export function isTextRecord(event: NostrEvent): boolean {
  const prose = getNoteProse(event).length;
  if (prose >= LONG_FORM_MIN_CHARS) return true;
  const hasAttachment =
    Boolean(getNoteVideo(event)) || Boolean(getNoteImage(event)) || Boolean(getNoteDocument(event));
  return !hasAttachment && prose >= TEXT_ONLY_MIN_CHARS;
}

/**
 * Format a unix timestamp as a human-readable date string.
 */
export function formatDate(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a unix timestamp as a short relative time string.
 */
export function formatRelativeTime(ts: number): string {
  const now = Date.now();
  const diff = now - ts * 1000;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (years > 0) return `${years}y ago`;
  if (months > 0) return `${months}mo ago`;
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'just now';
}
